"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  DataTable,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  DEFAULT_COMPLEX_OPTIONS,
  findComplexWords,
  groupLong,
  groupPlain,
  type PlainGroup,
  parseKeepList,
  replaceSpans,
} from "@/lib/tool-kit/style-checkers/complex-words";
import { OMIT } from "@/lib/tool-kit/style-checkers/plain-language-words";

/** English strings. A translated content file overrides these through `ui`. */
export const COMPLEX_WORD_FINDER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, an abstract or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "We will utilize a comprehensive methodology in order to facilitate participation. Prior to the commencement of the study, the investigators will endeavor to ascertain each participant's eligibility. Students who demonstrate sufficient proficiency will be given additional support.",
  minLabel: "Count a word as long from",
  minThree: "3 syllables (the fog-index definition)",
  minFour: "4 syllables (the longest words only)",
  usePlain: "Check the plain-language word list",
  skipQuotes: "Skip quoted passages",
  keepLabel: "Words to keep",
  keepHint:
    "Words or phrases you want left alone, separated by commas or new lines. Click Keep on a result to add it here.",
  keepPlaceholder: "methodology, participants",
  enterPrompt:
    "Paste or type some text to find long words and wording the plain-language guidelines would simplify.",
  results: "Results",
  words: "Words",
  complex: "Long words",
  share: "Share of all words",
  plainFound: "Plain-language matches",
  readingNone:
    "No long words or listed wording found. That is a good sign for readability, but a short word can still be the wrong one.",
  readingLow:
    "Fewer than one word in ten is long. For academic prose that is easy to read; the matches below are the only candidates for trimming.",
  readingMid:
    "Between one word in ten and one in five is long. That is typical of academic writing; replace a long word only where a shorter one means the same.",
  readingHigh:
    "More than one word in five is long. Dense vocabulary slows readers; look at the plain-language matches first, since each has a simpler word that usually means the same.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with complex words highlighted",
  legendPlain: "Has a plainer alternative",
  legendLong: "Long word",
  hoverHint:
    "Hover over a highlight to see the alternative or the syllable count.",
  plainHeading: "Simpler wording available",
  plainHint:
    "From the Federal Plain Language Guidelines. The list was written for government documents, so use judgement: some entries read as stiff in a research paper, and others are just right.",
  dirtyDozen: "Dirty dozen",
  dirtyDozenTitle:
    "One of the twelve words the guidelines say are most likely to weaken your writing",
  times: "one: time|other: times",
  useLabel: "Change to:",
  omitNote: "Often this can simply be cut.",
  adjustNote: "Check the ending fits your sentence after replacing.",
  keep: "Keep",
  longHeading: "Long words with no listed alternative",
  longHint:
    "Check each one: if a shorter word means the same, use it; if it is the exact term, keep it.",
  longTruncated: "Showing the {count} longest words.",
  colWord: "Word",
  colSyllables: "Syllables",
  colTimes: "Times",
  hoverPlain: "Plainer: {alternatives}",
  hoverLong: "{count} syllables",
  limits:
    "Syllables are counted by rule and are right for most, not all, words. Capitalised names, hyphenated compounds and ALL-CAPS words are never counted as long, and an -ing, -ed or -es ending does not add a syllable. Long words are not wrong; the checker marks them, you decide.",
} as const;

type Strings = typeof COMPLEX_WORD_FINDER_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
type MinSyllables = "3" | "4";

const TONES = { plain: "amber", long: "sky" } as const;
const MAX_LONG_ROWS = 40;
const PERCENT = { style: "percent", maximumFractionDigits: 1 } as const;

function reading(share: number, total: number, strings: Strings): string {
  if (total === 0) {
    return strings.readingNone;
  }
  if (share < 0.1) {
    return strings.readingLow;
  }
  return share <= 0.2 ? strings.readingMid : strings.readingHigh;
}

function PlainCard({
  group,
  strings,
  fmt,
  locale,
  onReplace,
  onKeep,
}: {
  group: PlainGroup;
  strings: Strings;
  fmt: Fmt;
  locale: string;
  onReplace: (group: PlainGroup, replacement: string) => void;
  onKeep: (key: string) => void;
}) {
  const options = group.alternatives.filter((alt) => alt !== OMIT);
  const canOmit = group.alternatives.includes(OMIT);
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-center gap-3">
        <code className="rounded bg-muted px-2 py-1 font-medium text-foreground text-sm">
          {group.text}
        </code>
        <span className={HINT}>
          {fmt(group.spans.length)}
          {tPlural(strings.times, locale, group.spans.length)}
        </span>
        {group.entry.dirtyDozen ? (
          <span
            className="rounded-full bg-rose-100 px-2 py-0.5 font-medium text-rose-800 text-xs dark:bg-rose-950 dark:text-rose-200"
            title={strings.dirtyDozenTitle}
          >
            {strings.dirtyDozen}
          </span>
        ) : null}
        <button
          className={`${BUTTON_SMALL} ms-auto`}
          onClick={() => onKeep(group.key)}
          type="button"
        >
          {strings.keep}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={HINT}>{strings.useLabel}</span>
        {options.map((alt) => (
          <button
            className={BUTTON_SMALL}
            key={alt}
            onClick={() => onReplace(group, alt)}
            type="button"
          >
            {alt}
          </button>
        ))}
        {canOmit ? <span className={HINT}>{strings.omitNote}</span> : null}
      </div>
      {group.entry.verb ? <p className={HINT}>{strings.adjustNote}</p> : null}
    </li>
  );
}

export default function ComplexWordFinderWidget() {
  const strings = useToolUi(COMPLEX_WORD_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [minSyllables, setMinSyllables] = useState<MinSyllables>("3");
  const [usePlainList, setUsePlainList] = useState(true);
  const [skipQuotes, setSkipQuotes] = useState(true);
  const [keepRaw, setKeepRaw] = useState("");
  const deferred = useDeferredValue(text);
  const keep = useMemo(() => parseKeepList(keepRaw), [keepRaw]);
  const report = useMemo(
    () =>
      findComplexWords(deferred, {
        ...DEFAULT_COMPLEX_OPTIONS,
        minSyllables: Number(minSyllables),
        usePlainList,
        skipQuotes,
        keep,
      }),
    [deferred, minSyllables, usePlainList, skipQuotes, keep]
  );
  const plainGroups = useMemo(() => groupPlain(report.plain), [report.plain]);
  const longGroups = useMemo(() => groupLong(report.long), [report.long]);
  const hasText = deferred.trim().length > 0;
  const share = report.words > 0 ? report.complex / report.words : 0;

  function replaceAll(group: PlainGroup, replacement: string) {
    if (text === deferred) {
      setText(replaceSpans(text, group.spans, replacement));
    }
  }

  function addKeep(key: string) {
    setKeepRaw((current) => (current.trim() ? `${current}, ${key}` : key));
  }

  const highlights: TextMatch[] = [
    ...report.plain.map((match) => ({
      start: match.start,
      end: match.end,
      category: "plain",
      title: t(strings.hoverPlain, {
        alternatives: match.alternatives.join(", "),
      }),
    })),
    ...report.long.map((word) => ({
      start: word.start,
      end: word.end,
      category: "long",
      title: t(strings.hoverLong, { count: fmt(word.syllables) }),
    })),
  ];

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="cwf-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.minLabel}
        name="cwf-min"
        onChange={setMinSyllables}
        options={[
          { value: "3", label: strings.minThree },
          { value: "4", label: strings.minFour },
        ]}
        value={minSyllables}
      />
      <div className="space-y-2">
        <Checkbox
          checked={usePlainList}
          label={strings.usePlain}
          onChange={setUsePlainList}
        />
        <Checkbox
          checked={skipQuotes}
          label={strings.skipQuotes}
          onChange={setSkipQuotes}
        />
      </div>
      <TextAreaField
        hint={strings.keepHint}
        id="cwf-keep"
        label={strings.keepLabel}
        onChange={setKeepRaw}
        placeholder={strings.keepPlaceholder}
        prose
        rows={2}
        value={keepRaw}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.complex}
                value={fmt(report.complex)}
              />
              <StatCard
                emphasis
                label={strings.share}
                value={fmt(share, PERCENT)}
              />
              <StatCard
                label={strings.plainFound}
                value={fmt(report.plain.length)}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
            <Notice>
              {reading(share, report.complex + report.plain.length, strings)}
            </Notice>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.amber}>
                {strings.legendPlain}
              </span>
              <span className={HIGHLIGHT_TONES.sky}>{strings.legendLong}</span>
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {plainGroups.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.plainHeading}</h3>
              <p className={HINT}>{strings.plainHint}</p>
              <ul className="space-y-3">
                {plainGroups.map((group) => (
                  <PlainCard
                    fmt={fmt}
                    group={group}
                    key={group.key}
                    locale={locale}
                    onKeep={addKeep}
                    onReplace={replaceAll}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {longGroups.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.longHeading}</h3>
              <p className={HINT}>{strings.longHint}</p>
              <DataTable
                head={[strings.colWord, strings.colSyllables, strings.colTimes]}
                rows={longGroups
                  .slice(0, MAX_LONG_ROWS)
                  .map((group) => [
                    group.text,
                    fmt(group.syllables),
                    fmt(group.spans.length),
                  ])}
              />
            </div>
          ) : null}
          {longGroups.length > MAX_LONG_ROWS ? (
            <p className={HINT}>
              {t(strings.longTruncated, { count: fmt(MAX_LONG_ROWS) })}
            </p>
          ) : null}
          <p className={HINT}>{strings.limits}</p>
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
